import { DataSource } from '@angular/cdk/collections';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { UiConstants } from 'core/constants';
import { PagedResult, PageLoadParams } from 'common/table/paged-result.interface';
import { EditingSupport } from 'common/table/editing-support.interface';
import { HasId } from 'common/table/has-id.interface';

// A custom data source for an Angular Material table that handles pagination and loading data from a server.
export abstract class TableDataSource<T extends HasId> extends DataSource<T> {
  protected itemsSubject = new BehaviorSubject<T[]>([]);

  private _pagedResult?: PagedResult<T>;
  private _paginator?: MatPaginator;
  private _editingSupport?: EditingSupport<T>;
  private loadParamsSubject = new Subject<PageLoadParams>();
  private loadSubscription: Subscription;

  data: T[] = [];

  constructor() {
    super();

    this.loadSubscription = this.loadParamsSubject
      .pipe(
        switchMap((pageLoadParams) =>
          this.fetchPage(pageLoadParams).pipe(
            catchError(() => {
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe((pagedResult) => {
        this.handlePagedResult(pagedResult);
      });
  }

  /*
  In the host component, use:

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  and

  this.dataSource.paginator = this.paginator;

  in ngAfterViewInit()
   */
  set paginator(value: MatPaginator | undefined) {
    this._paginator = value;

    if (value && this._pagedResult) {
      // pagedResult is already present
      this.applyPaginatorState(value, this._pagedResult);
    }
  }

  get paginator(): MatPaginator | undefined {
    return this._paginator;
  }

  get editingSupport(): EditingSupport<T> | undefined {
    return this._editingSupport;
  }

  set editingSupport(value: EditingSupport<T>) {
    this._editingSupport = value;
  }

  override connect(): Observable<T[]> {
    return this.itemsSubject.asObservable();
  }

  override disconnect(): void {
    this.loadSubscription.unsubscribe();
    this.loadParamsSubject.complete();
    this.itemsSubject.complete();
  }

  /*
  This has to be implemented by the child class. Usually, you want to fetch data from the backend here.
  */
  protected abstract fetchPage(properties: PageLoadParams): Observable<PagedResult<T>>;

  handlePagedResult(pagedResult: PagedResult<T>): void {
    this._pagedResult = pagedResult;
    this.data = pagedResult.items;
    this.itemsSubject.next(pagedResult.items);

    if (this.paginator) {
      // paginator is already present
      this.applyPaginatorState(this.paginator, pagedResult);
    }
  }

  private applyPaginatorState(paginator: MatPaginator, pagedResult: PagedResult<T>): void {
    paginator.pageIndex = pagedResult.page;
    paginator.pageSize = pagedResult.pageSize;
    paginator.length = pagedResult.totalItemCount;
  }

  refreshFromRoute(route: ActivatedRoute) {
    this.refreshFromQueryParams(route.snapshot.queryParamMap);
  }

  refreshFromQueryParams(queryParams: ParamMap) {
    this.loadParamsSubject.next({
      page: this.getNonNegativeIntegerQueryParam(queryParams, 'page', 0),
      pageSize: this.getPositiveIntegerQueryParam(
        queryParams,
        'pageSize',
        UiConstants.DEFAULT_PAGE_SIZE,
      ),
      sortBy: queryParams.get('sortBy') ?? undefined,
      sortDirection: queryParams.get('sortDirection') ?? undefined,
      additionalParams: this.getOtherQueryParams(queryParams),
    });
  }

  private getOtherQueryParams(queryParams: ParamMap): Record<string, string> {
    const excludedQueryParams = new Set(['page', 'pageSize', 'sortBy', 'sortDirection']);

    return Object.fromEntries(
      queryParams.keys
        .filter((key) => !excludedQueryParams.has(key))
        .map((key) => [key, queryParams.get(key)])
        .filter(([, value]) => value !== null && value !== undefined && value !== ''),
    ) as Record<string, string>;
  }

  private getNonNegativeIntegerQueryParam(
    queryParams: ParamMap,
    key: string,
    fallback: number,
  ): number {
    const value = Number(queryParams.get(key));
    return Number.isInteger(value) && value >= 0 ? value : fallback;
  }

  private getPositiveIntegerQueryParam(
    queryParams: ParamMap,
    key: string,
    fallback: number,
  ): number {
    const value = Number(queryParams.get(key));
    return Number.isInteger(value) && value > 0 ? value : fallback;
  }
}
